import useGuardedRealtimeList, { readListRows } from '../../realtime/useGuardedRealtimeList';
import { normalizeResponseData } from '../../redux/api/contracts';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchData, patchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls, selectUpdatedApis } from '../../redux/slices/apiUrlSlice';
import { Row, Col, Form, Button, Alert, Table, Card, Spinner } from 'react-bootstrap';
import { usePermissions } from '../../hooks/usePermissions';
// Import centralized date utilities
import { formatDateForForm, isDateField as isDateFieldUtil } from '../../utils/dateUtils';

const AdminDynamicTableEditor = () => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const updatedApis = useSelector(selectUpdatedApis);
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();
  const canViewDynamicTableEditor = canView('admin.dynamic-table-editor');
  const canEditDynamicTableEditor = canEdit('admin.dynamic-table-editor');
    // State management
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedTableUrl, setSelectedTableUrl] = useState('');
  const [inputId, setInputId] = useState('');
  const [fetchedData, setFetchedData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({});
  const [isDirty, setIsDirty] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loadedScope, setLoadedScope] = useState(null);
  const scope = JSON.stringify([selectedTable, inputId, selectedTableUrl]);
  useGuardedRealtimeList({ apiKey: selectedTable, enabled: canViewDynamicTableEditor && loadedScope === scope,
    params: updatedApis[selectedTable] ? { [selectedTable === 'generalProject' ? 'id' : 'projectID']: inputId } : undefined,
    blocked: loading || Object.values(isDirty).some(Boolean) || Object.values(saveStatus).some(value => value === 'saving'),
    decode: data => readListRows(normalizeResponseData(data, { keepOriginalKeys: true, includeMappedKeys: true, dedupeCaseVariantKeys: true })),
    apply: rows => {
      setFetchedData(rows);
      if (selectedItem) {
        const selectedId = selectedItem.ID ?? selectedItem.Id ?? selectedItem.id;
        const next = selectedId == null ? null : rows.find(row => String(row.ID ?? row.Id ?? row.id) === String(selectedId));
        setSelectedItem(next || null); setFormData(next ? { ...next } : {});
      }
    },
  });

  // Filter out certain APIs that shouldn't be in the dropdown
  const excludedApis = ['globalSearch', 'changeLog', 'activityLog', 'releaseForm', 'scheduleChangeLog'];
  const availableApis = Object.entries(apiUrls).filter(([key]) => !excludedApis.includes(key));

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  // Clear form when table or ID changes
  useEffect(() => {
    setFetchedData([]);
    setSelectedItem(null);
    setFormData({});
    setIsDirty({});
    setSaveStatus({});
    setError('');
    setSuccessMessage('');
    setSearchTerm('');
  }, [selectedTable, inputId]);

  // Determine if we should use 'id' or 'projectID' for the API call
  const getIdParameter = (tableName) => {
    return tableName === 'generalProject' ? 'id' : 'projectID';
  };

  // Build the API URL with the appropriate query parameter
  const buildApiUrl = (baseUrl, tableName, id) => {
    if (!id) return baseUrl;
    
    const isUpdated = updatedApis[tableName];
    if (!isUpdated) return baseUrl;
    
    const paramName = getIdParameter(tableName);
    return `${baseUrl}?${paramName}=${id}`;
  };

  // Handle table selection
  const handleTableChange = (e) => {
    const tableName = e.target.value;
    setSelectedTable(tableName);
    setSelectedTableUrl(apiUrls[tableName] || '');
  };

  // Fetch data from the selected API
  const handleFetchData = async () => {
    if (!selectedTableUrl) {
      setError('Please select a table');
      return;
    }

    if (!inputId.trim()) {
      setError('Please enter an ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const apiUrl = buildApiUrl(selectedTableUrl, selectedTable, inputId);
      console.log(`Fetching data from: ${apiUrl}`);
      
      const response = await dispatch(
        fetchData({
          url: apiUrl,
          contract: {
            response: {
              keepOriginalKeys: true,
              includeMappedKeys: true,
              dedupeCaseVariantKeys: true,
            },
          },
        })
      ).unwrap();
      
      setLoadedScope(scope);
      if (Array.isArray(response)) {
        if (response.length === 0) {
          setError(`No data found for ID: ${inputId}`);
          setFetchedData([]);
        } else {
          setFetchedData(response);
          // If only one item, auto-select it
          if (response.length === 1) {
            handleItemSelect(response[0]);
          }
        }
      } else if (response && typeof response === 'object') {
        setFetchedData([response]);
        handleItemSelect(response);
      } else {
        setError('Unexpected response format');
        setFetchedData([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to fetch data: ${err.message || err}`);
      setFetchedData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle item selection for editing
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setFormData({ ...item });
    setIsDirty({});
    setSaveStatus({});
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(prev => ({
      ...prev,
      [field]: true
    }));
  };

  // Handle field blur (save on blur)
  const handleFieldBlur = async (field) => {
    if (!canEditDynamicTableEditor || !isDirty[field] || !selectedItem) return;

    const newValue = formData[field];
    const originalValue = selectedItem[field];

    // Don't save if value hasn't actually changed
    if (newValue === originalValue) {
      setIsDirty(prev => ({
        ...prev,
        [field]: false
      }));
      return;
    }

    setSaveStatus(prev => ({
      ...prev,
      [field]: 'saving'
    }));

    try {
      // Build the patch URL and payload
      const patchUrl = buildApiUrl(selectedTableUrl, selectedTable, selectedItem.ID || selectedItem.id);
      const patchPayload = {
        ID: selectedItem.ID || selectedItem.id,
        [field]: newValue
      };

      console.log(`Patching field ${field} for ID ${selectedItem.ID || selectedItem.id}:`, patchPayload);

      await dispatch(patchData({ 
        url: patchUrl, 
        data: patchPayload 
      })).unwrap();

      // Update the selected item and fetched data
      const updatedItem = { ...selectedItem, [field]: newValue };
      setSelectedItem(updatedItem);
      
      setFetchedData(prev => 
        prev.map(item => 
          (item.ID || item.id) === (selectedItem.ID || selectedItem.id) 
            ? updatedItem 
            : item
        )
      );

      setSaveStatus(prev => ({
        ...prev,
        [field]: 'saved'
      }));

      setSuccessMessage(`${field} updated successfully`);

      // Clear dirty flag
      setIsDirty(prev => ({
        ...prev,
        [field]: false
      }));

      // Clear save status after a delay
      setTimeout(() => {
        setSaveStatus(prev => ({
          ...prev,
          [field]: ''
        }));
      }, 2000);

    } catch (err) {
      console.error(`Error saving field ${field}:`, err);
      setSaveStatus(prev => ({
        ...prev,
        [field]: 'error'
      }));
      setError(`Failed to save ${field}: ${err.message || err}`);
      
      // Reset the form field to original value
      setFormData(prev => ({
        ...prev,
        [field]: originalValue
      }));
      
      setTimeout(() => {
        setSaveStatus(prev => ({
          ...prev,
          [field]: ''
        }));
      }, 3000);
    }
  };  // Helper function to split camelCase and PascalCase strings
  const formatFieldLabel = (field) => {
    return field
      // Insert space before capital letters that follow lowercase letters or numbers
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      // Insert space before capital letters that are followed by lowercase letters (for cases like "XMLHttpRequest")
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      // Capitalize the first letter
      .replace(/^./, str => str.toUpperCase());
  };  // Helper function to check if a field should be treated as a date
  const isDateField = (fieldName) => {
    return isDateFieldUtil(fieldName);
  };

  // Helper function to format date values (remove time component)
  const formatDateValue = (value) => {
    if (!value) return '';
    return formatDateForForm(value);
  };
  // Helper function to check if a field should be highlighted based on search
  const shouldHighlight = (text) => {
    if (!searchTerm) return false;
    return text.toLowerCase().includes(searchTerm.toLowerCase());
  };

  // Helper function to check if a field name matches the search term
  // Checks both the original field name and the formatted label
  const fieldMatchesSearch = (fieldName) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const fieldLower = fieldName.toLowerCase();
    const formattedLabelLower = formatFieldLabel(fieldName).toLowerCase();
    
    // Check if search term matches the original field name (no spaces)
    const matchesOriginal = fieldLower.includes(searchLower);
    
    // Check if search term matches the formatted label (with spaces)
    const matchesFormatted = formattedLabelLower.includes(searchLower);
    
    // Also check if the search term without spaces matches the field name
    const searchNoSpaces = searchTerm.replace(/\s+/g, '').toLowerCase();
    const matchesNoSpaces = fieldLower.includes(searchNoSpaces);
    
    return matchesOriginal || matchesFormatted || matchesNoSpaces;
  };
  // Helper function to render highlighted text
  const renderHighlightedText = (text, originalFieldName = '') => {
    if (!searchTerm) {
      return <span>{text}</span>;
    }

    // Check if this field matches the search criteria
    if (originalFieldName && !fieldMatchesSearch(originalFieldName)) {
      return <span>{text}</span>;
    }

    // Create a case-insensitive regex for highlighting
    const searchRegex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    
    // Also check for matches when spaces are removed from search term
    const searchNoSpaces = searchTerm.replace(/\s+/g, '');
    const noSpacesRegex = searchNoSpaces.length > 0 ? 
      new RegExp(`(${searchNoSpaces.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi') : null;

    let parts = [text];
    
    // First, try to match the original search term
    if (searchRegex.test(text)) {
      parts = text.split(searchRegex);
    } 
    // If no direct match, try matching without spaces in the search term against the text
    else if (noSpacesRegex && noSpacesRegex.test(text.replace(/\s+/g, ''))) {
      // For this case, we need to be more creative with highlighting
      // We'll highlight individual words that match parts of the search term
      const searchWords = searchTerm.toLowerCase().split(/\s+/);
      let highlightedText = text;
      
      searchWords.forEach(word => {
        if (word.length > 0) {
          const wordRegex = new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
          highlightedText = highlightedText.replace(wordRegex, '<mark>$1</mark>');
        }
      });
      
      return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
    }

    return (
      <span>
        {parts.map((part, index) => {
          const isMatch = searchRegex.test(part);
          return isMatch ? (
            <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '0 2px' }}>
              {part}
            </mark>
          ) : (
            part
          );
        })}
      </span>
    );
  };
  // Render form fields based on the selected item
  const renderFormFields = () => {
    if (!selectedItem) return null;

    const excludedFields = ['ID', 'id', 'ProjectID', 'CreatedDate', 'ModifiedDate', 'CreatedBy', 'ModifiedBy'];
    const fields = Object.keys(selectedItem).filter(key => !excludedFields.includes(key));    // Filter fields based on search term
    const filteredFields = searchTerm 
      ? fields.filter(field => 
          fieldMatchesSearch(field) || 
          shouldHighlight(String(formData[field] || ''))
        )
      : fields;

    if (searchTerm && filteredFields.length === 0) {
      return (
        <Card className="mt-3">
          <Card.Header>
            <h5>Edit Record - ID: {selectedItem.ID || selectedItem.id}</h5>
          </Card.Header>
          <Card.Body>
            <Alert variant="info">No fields match your search term "{searchTerm}"</Alert>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card className="mt-3">
        <Card.Header>
          <h5>Edit Record - ID: {selectedItem.ID || selectedItem.id}</h5>
          {searchTerm && (
            <small className="text-muted">
              Showing {filteredFields.length} of {fields.length} fields matching "{searchTerm}"
            </small>
          )}
        </Card.Header>
        <Card.Body>          <Row>
            {filteredFields.map((field, index) => {
              const isDate = isDateField(field);
              const fieldValue = isDate ? formatDateValue(formData[field]) : (formData[field] || '');
              
              return (
                <Col md={3} key={field} className="mb-3">                  <Form.Group>
                    <Form.Label>
                      {renderHighlightedText(formatFieldLabel(field), field)}
                      {saveStatus[field] === 'saving' && (
                        <Spinner animation="border" size="sm" className="ms-2" />
                      )}
                      {saveStatus[field] === 'saved' && (
                        <span className="text-success ms-2">✓</span>
                      )}
                      {saveStatus[field] === 'error' && (
                        <span className="text-danger ms-2">✗</span>
                      )}
                    </Form.Label>
                    {typeof formData[field] === 'boolean' ? (
                      <Form.Check
                        type="checkbox"
                        checked={formData[field] || false}
                        disabled={!canEditDynamicTableEditor}
                        onChange={(e) => handleInputChange(field, e.target.checked)}
                        onBlur={() => handleFieldBlur(field)}
                      />
                    ) : (
                      <Form.Control
                        type={isDate ? 'date' : 
                              typeof selectedItem[field] === 'number' ? 'number' : 'text'}
                        value={fieldValue}
                        disabled={!canEditDynamicTableEditor}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        onBlur={() => handleFieldBlur(field)}
                        className={`${isDirty[field] ? 'border-warning' : ''} ${shouldHighlight(String(formData[field] || '')) ? 'bg-warning bg-opacity-25' : ''}`}
                      />
                    )}
                  </Form.Group>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  if (isPermissionsLoading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!canViewDynamicTableEditor) {
    return (
      <Alert variant="warning">
        You do not have permission to access the Dynamic Table Editor.
      </Alert>
    );
  }

  return (
    <div className="mt-4">
      <Card>
        <Card.Header>
          <h4>Dynamic Table Editor</h4>
          <p className="mb-0 text-muted">
            Select a table, enter an ID, and edit data dynamically. Changes are saved automatically when you move to the next field.
          </p>
        </Card.Header>
        <Card.Body>
          {/* Success and Error Messages */}
          {successMessage && (
            <Alert variant="success">{successMessage}</Alert>
          )}
          {error && (
            <Alert variant="danger">{error}</Alert>
          )}
          {!canEditDynamicTableEditor && (
            <Alert variant="info">
              You are viewing the Dynamic Table Editor in read-only mode based on your component policy permissions.
            </Alert>
          )}
          {/* Controls */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Select Table</Form.Label>
                <Form.Select 
                  value={selectedTable} 
                  onChange={handleTableChange}
                >
                  <option value="">Choose a table...</option>
                  {availableApis.map(([key, url]) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {selectedTable === 'generalProject' ? 'Job ID' : 'Project ID'}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={inputId}
                  onChange={(e) => setInputId(e.target.value)}
                  placeholder={`Enter ${selectedTable === 'generalProject' ? 'Job ID' : 'Project ID'}`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleFetchData();
                    }
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button 
                variant="primary" 
                onClick={handleFetchData}
                disabled={loading || !selectedTable || !inputId.trim()}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Fetching...
                  </>
                ) : (
                  'Fetch Data'
                )}
              </Button>
            </Col>
          </Row>

          {/* Search field - only show when form is visible */}
          {selectedItem && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Search Fields</Form.Label>
                  <Form.Control
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search field names or values..."
                    className="mb-2"
                  />
                  <Form.Text className="text-muted">
                    Search filters both field labels and values. Leave empty to show all fields.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                {searchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear Search
                  </Button>
                )}
              </Col>
            </Row>
          )}

          {/* Results Table */}
          {fetchedData.length > 0 && (
            <Card className="mt-3">
              <Card.Header>
                <h5>Found {fetchedData.length} record(s)</h5>
              </Card.Header>
              <Card.Body>
                {fetchedData.length > 1 ? (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Zone</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchedData.map((item, index) => (
                        <tr key={index}>
                          <td>{item.ID || item.id}</td>
                          <td>{item.Zone || 'N/A'}</td>
                          <td>
                            <Button 
                              size="sm" 
                              variant="outline-primary"
                              onClick={() => handleItemSelect(item)}
                              disabled={selectedItem && (selectedItem.ID || selectedItem.id) === (item.ID || item.id)}
                            >
                              {selectedItem && (selectedItem.ID || selectedItem.id) === (item.ID || item.id) ? 'Selected' : 'Select'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : null}
              </Card.Body>
            </Card>
          )}

          {/* Form for editing */}
          {renderFormFields()}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminDynamicTableEditor;
